import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

// Import your software logos from asset folder
const softwareLogos = [
  "/images/software/Automatic_Data_Processing.png",
  "/images/software/GustoReviews.png",
  "/images/software/xero.png",
  "/images/software/Intuit_QuickBooks.png",
  "/images/software/Paychex.png",
  "/images/software/square.png",
  "/images/software/Toast.png",
];

const logoNames = {
  Automatic_Data_Processing: "ADP",
  GustoReviews: "Gusto",
  xero: "Xero",
  Intuit_QuickBooks: "Intuit QuickBooks",
  Paychex: "Paychex",
  square: "Square",
  Toast: "Toast",
};
const logoName = (src) =>
  `${logoNames[src.split("/").pop().replace(/\.png$/, "")] || "Accounting software"} logo`;

export default function Expertise() {
  const location = useLocation();

  // Software Landscape state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  // Triple the array for seamless infinite effect
  const displayItems = [...softwareLogos, ...softwareLogos, ...softwareLogos];

  // Calculate item width (220px width + 24px gap = 244px total)
  const ITEM_WIDTH = 244;

  const nextSoftwareSlide = () => {
    setIsTransitioning(true);
    setCurrentIndex((prev) => prev + 1);
  };

  const handleTransitionEnd = () => {
    if (currentIndex >= softwareLogos.length) {
      setIsTransitioning(false);
      setCurrentIndex(currentIndex - softwareLogos.length);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextSoftwareSlide();
    }, 4000);
    return () => clearInterval(interval);
  }, []); // Fixed: removed softwareLogos.length dependency

  const services = [
    {
      id: "financial-reporting",
      title: "Comprehensive Financial Reporting",
      description:
        "We provide customized financial reports designed to support both operational and strategic decision-making. Our reporting framework includes detailed profit and loss statements with segmentation, enhanced balance sheets, and cash flow reports that provide full visibility into financial health and liquidity.",
      features: [
        "Customized Profit & Loss Statements",
        "Segment-wise financial reporting",
        "Enhanced Balance Sheet visibility",
        "Cash Flow & liquidity clarity",
      ],
    },
    {
      id: "operational-insights",
      title: "Operational Financial Insights",
      description:
        "We deliver structured operational reports that help businesses track performance across departments, identify inefficiencies, and improve day-to-day financial decision-making with accurate and timely data.",
      features: [
        "Department-wise performance tracking",
        "Operational KPI reporting",
        "Expense behavior analysis",
        "Real-time financial visibility",
      ],
    },
    {
      id: "management-reporting",
      title: "Management Reporting & Analysis",
      description:
        "Our management reporting solutions provide leadership teams with deep financial insights, helping identify trends, evaluate risks, and support strategic planning with data-driven clarity.",
      features: [
        "Executive-level reporting dashboards",
        "Trend & variance analysis",
        "Risk identification reports",
        "Decision-support insights",
      ],
    },
    {
      id: "strategic-intelligence",
      title: "Strategic Financial Intelligence",
      description:
        "We transform financial data into actionable intelligence that supports long-term planning, forecasting, and strategic execution across business functions.",
      features: [
        "Financial forecasting models",
        "Strategic planning support",
        "Revenue & cost intelligence",
        "Growth opportunity analysis",
      ],
    },
    {
      id: "profit-loss",
      title: "Profit & Loss Segmentation",
      description:
        "We provide detailed P&L segmentation that allows businesses to analyze profitability at product, service, or department level for better financial control.",
      features: [
        "Product/service-level P&L breakdown",
        "Cost center analysis",
        "Profitability tracking",
        "Revenue stream visibility",
      ],
    },
    {
      id: "cashflow",
      title: "Cash Flow & Liquidity Reporting",
      description:
        "Our cash flow reporting ensures businesses maintain strong liquidity visibility, helping manage operational stability and financial planning with confidence.",
      features: [
        "Cash inflow & outflow tracking",
        "Liquidity forecasting",
        "Working capital analysis",
        "Financial stability reporting",
      ],
    },
    {
      id: "balance-sheet",
      title: "Balance Sheet Enhancement",
      description:
        "We enhance balance sheet reporting by improving clarity, structure, and detail, ensuring stakeholders get a true and transparent financial position of the business.",
      features: [
        "Improved asset & liability clarity",
        "Structured financial presentation",
        "Equity breakdown analysis",
        "Audit-ready reporting format",
      ],
    },
  ];

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="bg-white min-h-screen pt-28">
      {/* HEADER - Added isolation to keep particles inside */}
      <div className="relative overflow-hidden bg-[#203468] py-16 lg:py-24 text-white isolate">
        <div className="absolute inset-0 grid-bg opacity-20"></div>
        <div className="absolute inset-0">
          <div className="gradient-beam"></div>
        </div>

        {/* Floating particles - Fixed to stay in header */}
        <div className="absolute inset-0 pointer-events-none">
          <span className="particle w-2 h-2 left-[10%] top-[20%]"></span>
          <span className="particle w-3 h-3 left-[70%] top-[40%]"></span>
          <span className="particle w-2 h-2 left-[50%] top-[70%]"></span>
          <span className="particle w-3 h-3 left-[30%] top-[80%]"></span>
        </div>

        <div className="absolute inset-0 backdrop-blur-md bg-white/5"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="font-serif text-5xl lg:text-7xl leading-tight mb-8">
              <span className="word">Financial Reporting</span>{" "}
              <span className="word text-blue-400 italic">Expertise</span>
            </h1>
            <p className="text-slate-300 text-xl leading-relaxed max-w-2xl fade-up delay-2">
              Comprehensive financial solutions designed to drive corporate
              success and strategic clarity with precision and innovation.
            </p>
            <div className="mt-8 fade-up delay-3">
              <Link
                to="/services"
                className="relative inline-block px-8 py-3 rounded-full bg-blue-500 border border-white text-white overflow-hidden group"
              >
                <span className="relative z-10">Explore Services</span>
                <span className="absolute inset-0 bg-blue-400 opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></span>
                <span className="shine"></span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* SERVICES */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="space-y-0 border-t border-[#203468]">
          {services.map((service, index) => (
            <div
              key={index}
              id={service.id}
              className="group border-b border-slate-200 hover:border-[#203468] transition-colors duration-500 flex flex-col lg:flex-row scroll-mt-32"
            >
              <div className="pt-12 lg:w-5/12 lg:pr-12">
                {/* <span className="font-serif text-6xl text-slate-200 group-hover:text-blue-600 transition-colors mb-6 block">
                  {String(index + 1).padStart(2, "0")}
                </span> */}
                <h2 className="font-serif text-3xl lg:text-4xl text-[#203468] mb-6">
                  {service.title}
                </h2>
              </div>
              <div className="py-12 lg:w-7/12 lg:pl-12 lg:border-l border-slate-100">
                <p className="text-slate-600 text-lg leading-relaxed font-light mb-10">
                  {service.description}
                </p>
                <h3 className="text-xs font-bold text-[#203468] mb-6 uppercase tracking-[0.2em]">
                  Core Capabilities
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                  {service.features.map((feature, fIndex) => (
                    <div
                      key={fIndex}
                      className="flex items-center text-slate-700"
                    >
                      <CheckCircle2 className="h-4 w-4 text-blue-600 mr-3 shrink-0" />
                      <span className="font-medium text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* SOFTWARE LANDSCAPE SECTION */}
        <section className="py-24 bg-white border-t border-slate-100 overflow-hidden mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-3xl md:text-4xl font-bold text-[#203468] mb-12 font-sans tracking-tight">
              Software Landscape
            </h3>

            <div className="relative w-full overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
              <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

              <div className="overflow-hidden">
                <div
                  className="flex gap-6"
                  style={{
                    transform: `translateX(-${currentIndex * ITEM_WIDTH}px)`,
                    transition: isTransitioning
                      ? "transform 0.5s ease-in-out"
                      : "none",
                  }}
                  onTransitionEnd={handleTransitionEnd}
                >
                  {displayItems.map((logo, idx) => (
                    <div
                      key={idx}
                      aria-hidden={idx >= softwareLogos.length ? "true" : undefined}
                      className="shrink-0 bg-white rounded-2xl flex items-center justify-center border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all"
                      style={{ width: "220px", height: "110px" }}
                    >
                      <img
                        src={logo}
  alt={logoName(logo)}
  className="w-auto h-auto max-w-[160px] max-h-[80px] object-contain p-3"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Dot Indicators - FORCED VISIBILITY */}
            <div
              className="flex justify-center items-center gap-3 mt-10 h-6 w-full"
              style={{ isolation: "isolate", zIndex: 100 }}
            >
              {softwareLogos.map((_, idx) => {
                const isActive = currentIndex % softwareLogos.length === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      setIsTransitioning(true);
                      setCurrentIndex(idx);
                    }}
                    // Using inline styles to override any external CSS interference
                    style={{
                      display: "block",
                      width: isActive ? "12px" : "10px",
                      height: isActive ? "12px" : "10px",
                      backgroundColor: isActive ? "#203468" : "#cbd5e1", // Deep Navy vs Slate-300
                      borderRadius: "50%",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      opacity: 1, // Force full opacity
                      transition: "all 0.3s ease",
                    }}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="mt-16 text-center bg-slate-50 p-16 border border-slate-200">
          <h2 className="font-serif text-3xl text-[#203468] mb-4">
            Require a bespoke solution?
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            Our team can design custom reporting solutions tailored to your
            business structure.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#203468] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#019BD7] transition-colors"
          >
            Contact a Partner <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <style jsx>{`
        .grid-bg {
          background-image:
            linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
            linear-gradient(
              90deg,
              rgba(255, 255, 255, 0.1) 1px,
              transparent 1px
            );
          background-size: 50px 50px;
        }

        @keyframes beam {
          0% {
            transform: translateX(-100%) skewX(-12deg);
          }
          100% {
            transform: translateX(200%) skewX(-12deg);
          }
        }

        .gradient-beam {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.1),
            transparent
          );
          transform: skewX(-12deg);
          animation: beam 8s infinite;
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) translateX(0px);
            opacity: 0;
          }
          50% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.5;
          }
        }

        .particle {
          position: absolute;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          animation: float 6s infinite ease-in-out;
        }

        @keyframes wordFadeUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .word {
          display: inline-block;
          animation: wordFadeUp 0.6s ease forwards;
          opacity: 0;
        }
        .word:nth-child(1) {
          animation-delay: 0.1s;
        }
        .word:nth-child(2) {
          animation-delay: 0.3s;
        }

        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .fade-up {
          animation: fadeUp 0.6s ease forwards;
          opacity: 0;
        }
        .delay-2 {
          animation-delay: 0.4s;
        }
        .delay-3 {
          animation-delay: 0.6s;
        }

        @keyframes shine {
          0% {
            left: -100%;
          }
          20% {
            left: 100%;
          }
          100% {
            left: 100%;
          }
        }

        .shine {
          position: absolute;
          top: 0;
          left: -100%;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
          );
          transform: skewX(-20deg);
          animation: shine 3s infinite;
        }
      `}</style>
    </div>
  );
}